import { useDispatch, useSelector } from "react-redux";
import { isElectronBridgeAvailable, useIPCBridge } from "../hooks/useIPCBridge";
import { useDedupEffect } from "../hooks/useDedupEffect";
import { useUpdatesService } from "hooks/useUpdatesService";
import Updater, { UpdaterContainerProps } from "lib/containers/Updater";
import { createFailureAction } from "state/reducer";
import { SplitLayout } from "components/SplitLayout";
import { IADPState } from "../Types/AppStateTypes";
import { Box } from "@enaep-ng/benchmark";
import { useResolveServiceStatus } from "hooks/useNetworkService";


let updateInProgress = false;

export default function UpdaterView() {
  const state = useSelector((state:{rootState:IADPState})=>state.rootState);
  const dispatch = useDispatch()
  const {checkOnlineServerAvailability}  = useResolveServiceStatus();
  const { checkForUpdates } = useIPCBridge();
  const { ReleaseStatus } = useUpdatesService();
  const notifications = useSelector((state:{rootState:IADPState})=>state.rootState.currentLanguageContent?.notifications);
  const getUpdateProps = (): UpdaterContainerProps => {
    if (state.updateState.updateEvents?.type === "update-available") {
      return {
        title: notifications?.updatesAvailableTitle as string,
        subTitle: notifications?.updateStart as string,
        progress: {
          variant: "info",
          level: 1,
        },
        message: notifications?.updatesEstimatedTimeText as string,
        type: "info",
      };
    } else if (state.updateState.updateEvents?.type === "download-progress") {
      return {
        title: notifications?.updatesAvailableTitle as string,
        subTitle: notifications?.updateDownload as string,
        progress: {
          variant: "warning",
          level: state.updateState.updateEvents?.message?.level as number,
        },
        message: `${notifications?.updatesEstimatedTimeText} <br/> ${notifications?.wait}`,
        type: "progress",
      };
    } else if (state.updateState.updateEvents?.type === "install-progress") {
      return {
        title: notifications?.updatesReadyTitle as string,
        subTitle: notifications?.updateInstall as string,
        progress: {
          variant:
            state.updateState.updateEvents?.message?.level === 100
              ? "success"
              : "info",
          level: state.updateState.updateEvents?.message?.level,
        },
        message: `${notifications?.updatesEstimatedTimeText} <br/> ${notifications?.wait}`,
        type: "progress",
      };
    } else if (state.updateState.updateEvents?.type === "prepare-for-update") {
      return {
        title: notifications?.updatePrep as string,
        subTitle:
          `${notifications?.updatesEstimatedTimeText} <br/> ${notifications?.wait}` as string,
        progress: {
          variant: "info",
          level: 0,
        },
        message: state.updateState.updateEvents?.message?.progress.toString(),
        type: "wait",
      };
    } else if (state.updateState.updateEvents?.type === "update-complete") {
      return {
        title: notifications?.updatesReadyTitle as string,
        subTitle: notifications?.restartInfo as string,
        progress: {
          variant: "success",
          level: 100,
        },
        message: notifications?.doNotCloseAppWarn as string,
        type: "info",
      };
    } else if (state.updateState.updateEvents?.type === "update-ready") {
      return {
        title: notifications?.updatesReadyTitle as string,
        subTitle: notifications?.updateInstall as string,
        progress: {
          variant: "info",
          level: 0,
        },
        message: notifications?.doNotCloseAppWarn as string,
        type: "wait",
      };
    } else if (
      state.updateState.updateEvents?.type === "update-not-available"
    ) {
      return {
        title: notifications?.updateNotFoundTitle as string,
        subTitle: notifications?.updateNotFoundSubTitle as string,
        progress: {
          variant: "success",
          level: 0,
        },
        message: notifications?.updateNotFoundSubTitle as string,
        type: "info",
      };
    } else {
      return {
        title: notifications?.updateChecking as string,
        subTitle: notifications?.wait as string,
        progress: {
          variant: "success",
          level: 0,
        },
        message: "",
        type: "wait",
      };
    }
  };

  useDedupEffect(() => {
    if (updateInProgress) return;
    updateInProgress = true;
    //On chromebooks only release version may change, all assets will be precached during the app loading stage
    //Electron may have app releated updates, thus, update flow including relese version is controlled at Electron level
    try {
      if(state.sessionState.networkConnectionState === "Connected"){
        checkOnlineServerAvailability().then((reachedTheServer)=>{
          reachedTheServer ? (isElectronBridgeAvailable() ? checkForUpdates() : ReleaseStatus()) : dispatch(createFailureAction("NoServerConnectionDetected"))
        })
      }
      else{
        dispatch(createFailureAction("NoInternetConnectionDetected"));
      }
    } 
    catch(e){
      dispatch(createFailureAction("UpdateError")); 
    }
    
  }, []);
  return (
    <SplitLayout>
      <Box sx={{ padding: '0 calc(66px * var(--zoom))'}}>
        <Updater {...getUpdateProps()} />
      </Box>
    </SplitLayout>
  );
}
